import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Users, Calendar, Flame, Compass, Sparkles, Heart, MessageCircle, Plus } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CommunityCard, EventCard, ChallengeCard, ProjectCard, ListingCard, PersonCard } from "@/components/cards";
import { Loader, SectionHeader, EmptyState, PrimaryButton, PassionBadge } from "@/components/ui-bits";
import { toast } from "sonner";

function greeting() {
  const h = new Date().getHours();
  return h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening";
}

const QUICK = [
  { to: "/communities", label: "Communities", icon: Users, c: "#55708F" },
  { to: "/events", label: "Events", icon: Calendar, c: "#8FAF9B" },
  { to: "/challenges", label: "Challenges", icon: Flame, c: "#FF6B00" },
  { to: "/explore", label: "Explore", icon: Compass, c: "#FFC94A" },
];

function Row({ children }) {
  return <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">{children}</div>;
}

function FeedPost({ p, onLike }) {
  return (
    <div className="pa-card p-5" data-testid={`feed-post-${p.id}`}>
      <div className="flex items-center gap-3 mb-3">
        <img src={fileUrl(p.author?.avatar)} alt="" className="h-10 w-10 rounded-full object-cover" />
        <div className="min-w-0">
          <p className="font-semibold text-[#071126] text-sm truncate">{p.author?.name}</p>
          <p className="text-xs text-[#55708F] truncate">in {p.community_name}</p>
        </div>
      </div>
      <p className="text-[#071126]/85 text-sm leading-relaxed">{p.text}</p>
      {p.image && <img src={fileUrl(p.image)} alt="" className="mt-3 rounded-xl w-full max-h-72 object-cover" />}
      <div className="flex items-center gap-4 mt-3 text-sm text-[#55708F]">
        <button onClick={() => onLike(p)} className="inline-flex items-center gap-1.5 hover:text-[#FF6B00] transition-colors" data-testid={`feed-like-${p.id}`}>
          <Heart className={`h-4 w-4 ${p.liked ? "fill-[#FF6B00] text-[#FF6B00]" : ""}`} />{p.like_count}
        </button>
        <span className="inline-flex items-center gap-1.5"><MessageCircle className="h-4 w-4" />{p.comment_count}</span>
      </div>
    </div>
  );
}

export default function Home() {
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/home").then((r) => setData(r.data)).catch(() => setData({})); }, []);

  const like = async (p) => {
    try {
      const { data: r } = await api.post(`/posts/${p.id}/like`);
      setData((d) => ({ ...d, feed: d.feed.map((x) => x.id === p.id ? { ...x, liked: r.liked, like_count: r.like_count } : x) }));
    } catch { toast.error("Could not update like"); }
  };

  if (!data) return <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"><Loader label="Loading your home" /></div>;

  const firstName = (user?.name || "there").split(" ")[0];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10 space-y-12">
      {/* Welcome */}
      <section className="pa-in">
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-[#071126]">{greeting()}, {firstName} <span className="inline-block">👋</span></h1>
        <p className="text-[#071126]/60 mt-2 text-lg">Your passions are waiting. What will you do today?</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6">
          {QUICK.map((q) => (
            <Link key={q.to} to={q.to} data-testid={`quick-${q.label.toLowerCase()}`} className="pa-card pa-card-hover p-4 flex items-center gap-3">
              <span className="h-10 w-10 rounded-xl grid place-items-center" style={{ background: `${q.c}1f` }}><q.icon className="h-5 w-5" style={{ color: q.c }} /></span>
              <span className="font-semibold text-[#071126] text-sm">{q.label}</span>
            </Link>
          ))}
        </div>
      </section>

      {/* My communities */}
      <section>
        <SectionHeader eyebrow="Your communities" title="Where you belong"
          action={<Link to="/communities" className="text-sm font-semibold text-[#FF6B00] inline-flex items-center gap-1 hover:gap-1.5 transition-all" data-testid="see-all-communities">See all<ArrowRight className="h-4 w-4" /></Link>} />
        {data.my_communities?.length ? (
          <Row>{data.my_communities.slice(0, 3).map((c) => <CommunityCard key={c.id} c={c} />)}</Row>
        ) : (
          <EmptyState icon={Users} title="You haven't joined a community yet" hint="Communities are the heart of PassionAte. Find your people and jump in."
            action={<PrimaryButton as={Link} to="/communities" data-testid="empty-join-community">Browse communities</PrimaryButton>} testid="empty-my-communities" />
        )}
      </section>

      {/* Recommended */}
      {data.rec_communities?.length > 0 && (
        <section>
          <SectionHeader eyebrow="For you" title="Recommended communities" subtitle="Based on your passions" />
          <Row>{data.rec_communities.slice(0, 3).map((c) => <CommunityCard key={c.id} c={c} />)}</Row>
        </section>
      )}

      {/* Community feed */}
      {data.feed?.length > 0 && (
        <section>
          <SectionHeader eyebrow="Community feed" title="Fresh from your communities" />
          <div className="grid gap-5 md:grid-cols-2">{data.feed.slice(0, 4).map((p) => <FeedPost key={p.id} p={p} onLike={like} />)}</div>
        </section>
      )}

      {/* Events */}
      <section>
        <SectionHeader eyebrow="Happening soon" title="Upcoming events"
          action={<Link to="/events" className="text-sm font-semibold text-[#FF6B00] inline-flex items-center gap-1" data-testid="see-all-events">See all<ArrowRight className="h-4 w-4" /></Link>} />
        <Row>{data.events?.slice(0, 3).map((e) => <EventCard key={e.id} e={e} />)}</Row>
      </section>

      {/* Challenges */}
      <section>
        <SectionHeader eyebrow="Get involved" title="Active challenges"
          action={<Link to="/challenges" className="text-sm font-semibold text-[#FF6B00] inline-flex items-center gap-1" data-testid="see-all-challenges">See all<ArrowRight className="h-4 w-4" /></Link>} />
        <Row>{data.challenges?.slice(0, 3).map((c) => <ChallengeCard key={c.id} c={c} />)}</Row>
      </section>

      {/* People */}
      {data.people?.length > 0 && (
        <section>
          <SectionHeader eyebrow="Your people" title="Connect with similar passions" />
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{data.people.slice(0, 6).map((p) => <PersonCard key={p.id} p={p} />)}</div>
        </section>
      )}

      {/* Featured projects */}
      {data.projects?.length > 0 && (
        <section>
          <SectionHeader eyebrow="Showcase" title="Featured creations"
            action={<Link to="/explore" className="text-sm font-semibold text-[#FF6B00] inline-flex items-center gap-1">Explore<ArrowRight className="h-4 w-4" /></Link>} />
          <div className="grid gap-5 grid-cols-2 lg:grid-cols-4">{data.projects.slice(0, 4).map((p) => <ProjectCard key={p.id} p={p} />)}</div>
        </section>
      )}

      {/* Marketplace */}
      {data.listings?.length > 0 && (
        <section>
          <SectionHeader eyebrow="Opportunity" title="From the marketplace"
            action={<Link to="/marketplace" className="text-sm font-semibold text-[#FF6B00] inline-flex items-center gap-1" data-testid="see-all-marketplace">Browse<ArrowRight className="h-4 w-4" /></Link>} />
          <div className="grid gap-5 grid-cols-2 lg:grid-cols-4">{data.listings.slice(0, 4).map((l) => <ListingCard key={l.id} l={l} />)}</div>
        </section>
      )}
    </div>
  );
}
